import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ---------------------------------------------------------------------------
// Slot group definitions
// ---------------------------------------------------------------------------

// Benefit card: icon (image) + title + description. Kept as separate
// benefits so the operator can toggle each one independently.
const benefit1Def: SlotGroupDefinition = {
  label: 'Benefit 1',
  fields: {
    icon: { label: 'Icon', type: 'image', defaultValue: '' },
    title: { label: 'Title', type: 'text', defaultValue: 'Waived Application Fee' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Skip the standard application fee when you show proof of employment from a preferred employer.',
    },
  },
};

const benefit2Def: SlotGroupDefinition = {
  label: 'Benefit 2',
  fields: {
    icon: { label: 'Icon', type: 'image', defaultValue: '' },
    title: { label: 'Title', type: 'text', defaultValue: 'Waived Admin Fee' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'The administrative fee is waived for qualifying preferred employer applicants.',
    },
  },
};

// Bottom CTA link — points at the contact page by default so leads flow through
// the standard inquiry funnel.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us to Verify Eligibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PreferredEmployerProgram() {
  const benefit1 = useSlotGroup('preferredemployerprogram.benefit_1', benefit1Def);
  const benefit2 = useSlotGroup('preferredemployerprogram.benefit_2', benefit2Def);
  const ctaLink = useSlotGroup('preferredemployerprogram.cta_link', ctaLinkDef);

  const benefits: Array<{
    path: string;
    def: SlotGroupDefinition;
    data: Record<string, string | null>;
  }> = [
    { path: 'preferredemployerprogram.benefit_1', def: benefit1Def, data: benefit1 },
    { path: 'preferredemployerprogram.benefit_2', def: benefit2Def, data: benefit2 },
  ];

  return (
    <section className="pep-section">
      <div className="pep-container">
        {/* Intro */}
        <div className="pep-intro">
          <RichTextContent
            path="preferredemployerprogram.title"
            as="h2"
            className="pep-title"
          />
          <RichTextContent
            path="preferredemployerprogram.description"
            as="div"
            className="pep-description"
          />
          <div className="pep-divider" />
        </div>

        {/* Benefits */}
        <div className="pep-benefits">
          {benefits.map(({ path, def, data }) => (
            <EditableSlotGroup key={path} groupPath={path} definition={def}>
              <div className="pep-benefit-card">
                <div className="pep-benefit-icon">
                  {data.icon ? (
                    <VibesitesImage
                      url={data.icon}
                      alt=""
                      className="pep-benefit-icon-img"
                      sizes="56px"
                    />
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <h3 className="pep-benefit-title">{data.title || ''}</h3>
                <p className="pep-benefit-desc">{data.description || ''}</p>
              </div>
            </EditableSlotGroup>
          ))}
        </div>

        {/* Participating employers — a single richtext list keeps the editor
            experience simple (one field to edit the full roster) while letting
            operators add, remove, or reorder companies freely. Styled as a
            multi-column list to fit long lists on wide screens. */}
        <div className="pep-employers-block">
          <RichTextContent
            path="preferredemployerprogram.employers_heading"
            as="h3"
            className="pep-section-heading"
          />
          <RichTextContent
            path="preferredemployerprogram.employers_list"
            as="div"
            className="pep-employers-list"
          />
        </div>

        {/* Verification / disclaimer */}
        <RichTextContent
          path="preferredemployerprogram.disclaimer"
          as="p"
          className="pep-disclaimer"
        />

        {/* CTA */}
        <div className="pep-cta-wrap">
          <EditableSlotGroup
            groupPath="preferredemployerprogram.cta_link"
            definition={ctaLinkDef}
          >
            <a
              href={ctaLink.url || '/contact'}
              target={ctaLink._target || '_self'}
              className="pep-cta-btn"
            >
              {ctaLink.label || 'Contact Us to Verify Eligibility'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Schema-lint anchors — the benefit wrappers above use dynamic
            groupPath values inside a .map(), so the static linter can't find
            the canonical literal paths. These hidden anchors carry the literal
            paths so slot-group-missing-editable-wrapper is satisfied. They
            never render visibly and never receive clicks — the interactive
            wrappers are the ones inside the .map(). */}
        <div className="hidden" style={{ display: 'none' }} aria-hidden="true">
          <EditableSlotGroup groupPath="preferredemployerprogram.benefit_1" definition={benefit1Def}><span /></EditableSlotGroup>
          <EditableSlotGroup groupPath="preferredemployerprogram.benefit_2" definition={benefit2Def}><span /></EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pep-section {
          padding: 3rem 0 4rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .pep-section { padding: 4rem 0 5rem; }
        }
        .pep-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .pep-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .pep-container { padding: 0 2rem; } }

        .pep-intro {
          text-align: center;
          max-width: 60rem;
          margin: 0 auto 3rem;
        }
        .pep-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
        }
        @media (min-width: 768px) {
          .pep-title { font-size: 1.875rem; }
        }
        @media (min-width: 1024px) {
          .pep-title { font-size: 2.25rem; }
        }
        .pep-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
          max-width: 44rem;
          margin-left: auto;
          margin-right: auto;
        }
        .pep-description p { margin: 0 0 0.75rem; }
        .pep-description p:last-child { margin-bottom: 0; }

        .pep-divider {
          width: 4rem;
          height: 3px;
          margin: 1.5rem auto 0;
          background: var(--vibe-color-primary);
          opacity: 0.5;
          border-radius: 9999px;
        }

        /* Benefits row */
        .pep-benefits {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-bottom: 4rem;
        }
        @media (min-width: 768px) {
          .pep-benefits { grid-template-columns: repeat(2, 1fr); max-width: 44rem; margin-left: auto; margin-right: auto; }
        }
        .pep-benefit-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2rem 1.5rem;
          background: white;
          border: 1px solid var(--vibe-color-border);
          border-radius: var(--vibe-radius-lg, 12px);
          height: 100%;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .pep-benefit-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
        }
        .pep-benefit-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          margin-bottom: 1rem;
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
          overflow: hidden;
        }
        .pep-benefit-icon-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .pep-benefit-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.5rem;
        }
        .pep-benefit-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        /* Employers block */
        .pep-employers-block {
          margin-bottom: 2.5rem;
          background: white;
          border: 1px solid var(--vibe-color-border);
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem 1.75rem 1.5rem;
        }
        @media (min-width: 768px) {
          .pep-employers-block { padding: 2.5rem 2.5rem 2rem; }
        }
        .pep-section-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          text-align: center;
          color: var(--vibe-color-text);
          margin: 0 0 1.75rem;
        }
        .pep-employers-list {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text);
        }
        .pep-employers-list ul {
          list-style: none;
          margin: 0;
          padding: 0;
          columns: 1;
          column-gap: 2.5rem;
        }
        @media (min-width: 640px) {
          .pep-employers-list ul { columns: 2; }
        }
        @media (min-width: 1024px) {
          .pep-employers-list ul { columns: 3; }
        }
        .pep-employers-list li {
          position: relative;
          padding: 0.4rem 0 0.4rem 1.1rem;
          font-size: 0.95rem;
          line-height: 1.5;
          break-inside: avoid;
        }
        .pep-employers-list li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.85rem;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }
        .pep-employers-list p {
          margin: 0 0 1rem;
        }

        /* Disclaimer */
        .pep-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-align: center;
          max-width: 44rem;
          margin: 0 auto 2rem;
        }

        /* CTA */
        .pep-cta-wrap {
          display: flex;
          justify-content: center;
        }
        .pep-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          text-decoration: none;
          color: var(--vibe-color-text-on-secondary, white);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .pep-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerProgram',
  displayName: 'Preferred Employer Program',
  tokens: {
    'preferredemployerprogram.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: '<p>Preferred Employer Program</p>',
    },
    'preferredemployerprogram.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Preferred employers at <span data-type="field-token" data-field="property_name"></span> qualify for a waived application fee and waived admin fee. If you are a first responder, educator, military member, law enforcement officer, or work for one of our partner organizations below, contact us to verify eligibility.</p>',
    },
    'preferredemployerprogram.employers_heading': {
      label: 'Employers Heading',
      type: 'richtext',
      defaultValue: '<p>Preferred Employers</p>',
    },
    'preferredemployerprogram.employers_list': {
      label: 'Employers List',
      description:
        'Bulleted list of participating employers. Edit the list to add, remove, or reorder companies for this property.',
      type: 'richtext',
      defaultValue:
        '<ul><li>First responders</li><li>Educators</li><li>Military</li><li>Law enforcement</li><li>Delta Airlines</li><li>Piedmont Healthcare</li><li>Henry County</li><li>City of McDonough</li><li>Alpla</li></ul>',
    },
    'preferredemployerprogram.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Program benefits are subject to change and require valid proof of employment at move-in. Contact our leasing office to confirm current eligibility and available offers.</p>',
    },
  },
  slotGroups: {
    'preferredemployerprogram.benefit_1': benefit1Def,
    'preferredemployerprogram.benefit_2': benefit2Def,
    'preferredemployerprogram.cta_link': ctaLinkDef,
  },
};

(PreferredEmployerProgram as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PreferredEmployerProgram;
